import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useTranslation } from "react-i18next";
// hooks
import { useActions } from "../../../../../common/hooks/useActions";
// actions
import { swissTravelPassActionCreators, initialPassenger } from "../swissTravelPassModule";
// helpers
import { getUpdatedArray } from "../../../../../common/utils/helpers/getUpdatedArray";
// icons
import { closeDarkIcon, plusIcon } from "../../../../../common/vector";
// components
import AppFormInput from "../../../../../components/form/AppFormInputs/AppFormInput";
import AppDatePickerCustom from "../../../../../components/AppDatePickerCustom";

export default function SwissTravelPassPassengersForm() {
  const { t } = useTranslation();

  // states
  const travelPassPassengerList = useSelector((state: any) => state.swissTravelPass.travelPassPassengerList);
  const countries = useSelector((state: any) => state.swissTravelPass.countries);
  const showValidationErrors = useSelector((state: any) => state.swissTravelPass.showValidationErrors);
  // actions
  const updateSwissTravelPassengers = useActions(swissTravelPassActionCreators?.updateSwissTravelPassengersAction, []);

  const handlePassengerFirstNameChange = (event: any, index: number) => {
    const updatedList = getUpdatedArray(travelPassPassengerList, index, "firstName", event.target.value);
    updateSwissTravelPassengers(updatedList);
  };

  const handlePassengerLastNameChange = (event: any, index: number) => {
    const updatedList = getUpdatedArray(travelPassPassengerList, index, "lastName", event.target.value);
    updateSwissTravelPassengers(updatedList);
  };

  const handlePassengerCountryChange = (index: number, event: any) => {
    const updatedList = getUpdatedArray(travelPassPassengerList, index, "countryOfResidence", event);
    updateSwissTravelPassengers(updatedList);
  };

  const birthdateChange = (date: any, index: number) => {
    const updatedList = getUpdatedArray(travelPassPassengerList, index, "birthDate", date);
    updateSwissTravelPassengers(updatedList);
  };

  const removePassenger = (index: number) => {
    const updatedList = travelPassPassengerList?.filter((passenger: any, i: number) => i !== index);
    updateSwissTravelPassengers(updatedList);
  };

  const addPassengerToList = () => {
    updateSwissTravelPassengers([...travelPassPassengerList, { ...initialPassenger, hasRemove: true }]);
  };

  return (
    <Box sx={{ display: "flex" }} pb={3} pt={{ xs: 5, sm: 3 }}>
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          justifyContent: "center"
        }}
      >
        <Box className="passengers-title" pb={2}>
          {t("transport.passengers")} *
        </Box>
        <Box sx={{ display: { xs: "none", sm: "none", md: "flex" } }}>
          <Grid container columnSpacing={{ xs: 1 }}>
            <Grid item xs={2.5} className="passengers-subtitle" pb={1}>
              {t("transport.formFirstName")}
            </Grid>
            <Grid item xs={2.5} className="passengers-subtitle" pb={1}>
              {t("transport.formLastName")}
            </Grid>
            <Grid item xs={3} className="passengers-subtitle" pb={1}>
              {t("transport.formCountry")}
            </Grid>
            <Grid item xs={4} className="passengers-subtitle" pb={1}>
              {t("transport.formBirthdate")}
            </Grid>
          </Grid>
        </Box>
        {travelPassPassengerList?.length > 0 &&
          travelPassPassengerList?.map((passenger: any) => {
            let index = travelPassPassengerList.indexOf(passenger);
            const shouldHighlightFirstName =
              showValidationErrors && (travelPassPassengerList[index]?.firstName?.length ?? 0) < 2;
            const shouldHighlightLastName =
              showValidationErrors && (travelPassPassengerList[index]?.lastName?.length ?? 0) < 2;
            const shouldHighlightCountry =
              showValidationErrors &&
              (travelPassPassengerList[index]?.countryOfResidence === null ||
                travelPassPassengerList[index]?.countryOfResidence === undefined ||
                travelPassPassengerList[index]?.countryOfResidence === "");
            return (
              <Grid container columnSpacing={{ xs: 1 }} mb={2} key={index}>
                <Grid item md={2.5} sm={12} xs={12} mb={{ xs: 2, md: 0 }}>
                  <AppFormInput
                    placeholder={t("transport.formFirstName")}
                    name={"firstName"}
                    type="string"
                    disabled={false}
                    value={travelPassPassengerList[index]?.firstName}
                    onChange={(event: any) => {
                      handlePassengerFirstNameChange(event, index);
                    }}
                    required={true}
                    error={shouldHighlightFirstName}
                  />
                </Grid>
                <Grid item md={2.5} sm={12} xs={12} mb={{ xs: 2, md: 0 }}>
                  <AppFormInput
                    placeholder={t("transport.formLastName")}
                    name={"lastName"}
                    type="string"
                    disabled={false}
                    value={travelPassPassengerList[index]?.lastName}
                    onChange={(event: any) => {
                      handlePassengerLastNameChange(event, index);
                    }}
                    required={true}
                    error={shouldHighlightLastName}
                  />
                </Grid>
                <Grid item md={3} sm={12} xs={12} mb={{ xs: 2, md: 0 }}>
                  <Select
                    name="countryOfResidence"
                    sx={{
                      width: "100%",
                      "& .MuiSelect-select": {
                        color: travelPassPassengerList[index]?.countryOfResidence ? "inherit" : "#999"
                      },
                      ...(shouldHighlightCountry
                        ? {
                            border: "0.75px solid rgba(240, 82, 82, 0.1) !important",
                            borderLeft: "5px solid #F05252 !important",
                            backgroundColor: "rgba(240, 82, 82, 0.1) !important"
                          }
                        : {})
                    }}
                    value={travelPassPassengerList[index]?.countryOfResidence || ""}
                    onChange={event => handlePassengerCountryChange(index, event.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      {t("transport.selectCountryPlaceholder")}
                    </MenuItem>
                    {Object.entries(countries || {}).map(([code, name]: any) => (
                      <MenuItem key={code} value={JSON.stringify({ code, name })}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Box sx={{ display: { sm: "flex", md: "none", fontSize: "11px" } }} mt={0.5} ml={0.5}>
                    {t("transport.formCountry")}
                  </Box>
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "column", md: "row" },
                      alignItems: "center",
                      justifyContent: { sm: "space-between", md: "flex-start" }
                    }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <AppDatePickerCustom
                        selectedDate={passenger?.birthDate}
                        setSelectedDate={date => date != null && birthdateChange(date, index)}
                      />

                      <Box sx={{ display: { sm: "flex", md: "none", fontSize: "11px" } }} mt={0.5} ml={0.5}>
                        {t("transport.formBirthdate")}
                      </Box>
                    </Box>

                    <Grid
                      item
                      ml={{ xs: 0, sm: 0, md: 3 }}
                      mt={{ xs: 2.5, sm: 2.5, md: 0 }}
                      sx={{
                        display: { xs: "flex", sm: "flex", md: "block" },
                        width: { xs: "100%", sm: "100%", md: "auto" }
                      }}
                    >
                      <Box
                        onClick={() => removePassenger(index)}
                        className="passenger-remove"
                        style={passenger?.hasRemove ? {} : { opacity: 0, pointerEvents: "none" }}
                      >
                        <img src={closeDarkIcon?.default} alt="close-dark" />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid sx={{ display: { sm: "flex", md: "none" }, width: "100%" }} mt={1.5} mb={1}>
                  <Box sx={{ display: "flex", height: "1px", backgroundColor: "#d3d3d3", width: "100%" }} />
                </Grid>
              </Grid>
            );
          })}
        <Box onClick={addPassengerToList} sx={{ cursor: "pointer", width: "24px" }}>
          <img src={plusIcon?.default} alt="close-dark" height={24} width={24} />
        </Box>
      </Grid>
    </Box>
  );
}
